import { useCallback, useEffect, useMemo, useRef } from "react";
import { getWebSocketUrl } from "../config";
import { useWebSocket } from "./useWebSocket";

const PRESENCE_PING_MS = 10000;

/**
 * Long-lived user inbox WebSocket for chat-list activity and notifications.
 *
 * @param {number|string|null|undefined} userId
 * @param {{
 *   onInboxMessage?: (message: object) => void,
 *   onChatRemoved?: (chatId: number) => void,
 *   onConnectionNotification?: (connection: object) => void,
 *   onReconnect?: () => void,
 *   onPresence?: (presence: { user_id: number, last_online: string }) => void,
 * }} [handlers]
 * @param {{ enablePresence?: boolean }} [options]
 * @returns {void}
 */
export function useInboxSocket(userId, handlers = {}, options = {}) {
  const onInboxMessageRef = useRef(handlers.onInboxMessage);
  const onChatRemovedRef = useRef(handlers.onChatRemoved);
  const onConnectionNotificationRef = useRef(handlers.onConnectionNotification);
  const onReconnectRef = useRef(handlers.onReconnect);
  const onPresenceRef = useRef(handlers.onPresence);
  const enablePresence = options.enablePresence !== false;

  onInboxMessageRef.current = handlers.onInboxMessage;
  onChatRemovedRef.current = handlers.onChatRemoved;
  onConnectionNotificationRef.current = handlers.onConnectionNotification;
  onReconnectRef.current = handlers.onReconnect;
  onPresenceRef.current = handlers.onPresence;

  const wsUrl = useMemo(() => {
    if (!userId) return null;
    return getWebSocketUrl(`/ws/inbox/${userId}/?user_id=${userId}`);
  }, [userId]);

  const handleMessage = useCallback((data) => {
    if (!data?.type) return;
    if (data.type === "inbox_message" && data.data) {
      onInboxMessageRef.current?.(data.data);
      return;
    }
    if (data.type === "inbox_chat_removed") {
      const chatId = Number(data.data?.chat_id);
      if (Number.isFinite(chatId)) {
        onChatRemovedRef.current?.(chatId);
      }
      return;
    }
    if (data.type === "notification_connection" && data.data) {
      onConnectionNotificationRef.current?.(data.data);
      return;
    }
    if (data.type === "presence") {
      const userId = Number(data.user_id);
      const lastOnline = data.last_online || "";
      if (Number.isFinite(userId) && userId > 0 && lastOnline) {
        onPresenceRef.current?.({ user_id: userId, last_online: lastOnline });
      }
    }
  }, []);

  const handleOpen = useCallback(({ isReconnect }) => {
    if (isReconnect) {
      onReconnectRef.current?.();
    }
  }, []);

  const { send } = useWebSocket(wsUrl, handleMessage, { onOpen: handleOpen });

  useEffect(() => {
    if (!userId || !enablePresence) return undefined;
    const ping = () => send({ type: "presence_ping" });
    ping();
    const id = setInterval(ping, PRESENCE_PING_MS);
    return () => clearInterval(id);
  }, [userId, enablePresence, send]);
}
